(() => {
  if (customElements.get('mr-newsletter-gift')) return;
  let claimInProgress = false;
  const uniqueCodes = values => [...new Map(values.filter(Boolean).map(code => [code.toLowerCase(), code])).values()];

  class NewsletterGift extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const data = this.querySelector('[data-gift-config]');
      if (!data) return;
      try { this.config = JSON.parse(data.textContent); } catch { return; }
      this.select = this.querySelector('[data-gift-variant]');
      this.button = this.querySelector('[data-gift-claim]');
      this.status = this.querySelector('[data-gift-status]');
      this.bagLink = this.querySelector('[data-gift-bag]');
      this.codeInput = this.querySelector('[data-gift-code-value]');
      this.copyButton = this.querySelector('[data-gift-copy]');
      this.copyStatus = this.querySelector('[data-gift-copy-status]');
      this.shopLink = this.querySelector('[data-gift-shop]');
      if (!this.select || !this.config.code || !this.config.campaign) return;
      this.storageKey = 'moonreed:gift-choice:' + this.config.campaign;
      // Remember only the chosen variant across Shopify's native signup/captcha flow.
      try {
        const selected = sessionStorage.getItem(this.storageKey);
        const index = [...this.select.options].findIndex(option => option.value === selected && option.dataset.available === 'true');
        if (index >= 0) this.select.selectedIndex = index;
      } catch {}
      if (this.option?.dataset.available !== 'true') {
        const index = [...this.select.options].findIndex(option => option.dataset.available === 'true');
        if (index >= 0) this.select.selectedIndex = index;
      }
      this.select.addEventListener('change', () => this.sync(), {signal:this.abort.signal});
      this.button?.addEventListener('click', () => this.claim(), {signal:this.abort.signal});
      this.copyButton?.addEventListener('click', () => this.copyCode(), {signal:this.abort.signal});
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); }
    get option() { return this.select?.options[this.select.selectedIndex]; }
    sync() {
      const option = this.option;
      if (!option) return;
      try { sessionStorage.setItem(this.storageKey, option.value); } catch {}
      const image = this.querySelector('[data-gift-image]');
      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.alt = this.querySelector('.mr-gift-product__title').textContent.trim() + ' — ' + option.textContent.trim();
      }
      this.querySelector('[data-gift-price]').textContent = option.dataset.price;
      if (this.shopLink) {
        const url = this.shopLink.dataset.giftProductUrl;
        this.shopLink.href = url + (url.includes('?') ? '&' : '?') + 'variant=' + encodeURIComponent(option.value);
      }
      const available = option.dataset.available === 'true';
      this.querySelector('[data-gift-availability]').textContent = available ? '' : 'This gift is temporarily unavailable. You can still join the list.';
      if (this.button) this.button.disabled = !available || !this.config.unlocked || this.config.editor || !!this.busy || !!this.reviewBag;
    }
    async copyCode() {
      if (!this.codeInput || !this.copyButton || this.copyButton.disabled) return;
      this.copyButton.disabled = true;
      try {
        if (!window.navigator?.clipboard?.writeText) throw new Error('Copy manually');
        await window.navigator.clipboard.writeText(this.codeInput.value);
        this.copyButton.textContent = 'Copied';
        this.copyStatus.textContent = 'Code copied. Apply it at checkout with an eligible item.';
      } catch {
        this.codeInput.focus();
        this.codeInput.select();
        this.copyButton.textContent = 'Copy code';
        this.copyStatus.textContent = 'Select and copy the code above, then apply it at checkout.';
      } finally { this.copyButton.disabled = false; }
    }
    async request(url, payload) {
      const response = await fetch(url, payload ? {
        method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify(payload)
      } : {cache:'no-store', credentials:'same-origin', headers:{Accept:'application/json'}});
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(typeof result.description === 'string' ? result.description : 'We could not update your bag. Please try again.');
        error.responseKnown = true;
        throw error;
      }
      return result;
    }
    cart() { return this.request(this.config.cartUrl + '.js'); }
    codes(cart, applicableOnly = false) {
      if (Array.isArray(cart.discount_codes)) {
        return uniqueCodes(cart.discount_codes.filter(discount => !applicableOnly || discount.applicable === true).map(discount => discount.code));
      }
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
      return uniqueCodes(applications.filter(application => application?.type === 'discount_code').map(application => application.title));
    }
    hasCode(cart, code) { return this.codes(cart, true).some(value => value.toLowerCase() === code.toLowerCase()); }
    gifts(cart) { return (cart.items || []).filter(item => item.properties?._mr_newsletter_gift === this.config.campaign); }
    isFree(line) {
      return line && String(line.product_id) === String(this.config.productId) && line.quantity === 1 && typeof line.final_line_price === 'number' && line.final_line_price === 0;
    }
    message(text, showBag = false) {
      if (this.status) this.status.textContent = text;
      if (this.bagLink) this.bagLink.hidden = !showBag;
    }
    async publishCart(cart, open = false) {
      const drawer = document.getElementById('cart-drawer');
      const sections = drawer?.dataset.mrSectionId ? [drawer.dataset.mrSectionId] : [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles:true,detail:{sections}}));
      const ids = [...new Set(sections.filter(Boolean))].slice(0,5);
      if (ids.length) {
        const rendered = await this.request(this.config.cartUrl + '?sections=' + encodeURIComponent(ids.join(',')));
        if (ids.some(id => !rendered[id])) throw new Error('Bag display is unavailable.');
        cart.sections = rendered;
      }
      if (open && drawer && typeof drawer.show === 'function') {
        const popup = this.closest('newsletter-popup');
        if (popup?.open && typeof popup.hide === 'function') await popup.hide();
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles:true,detail:{baseEvent:open ? 'variant:add' : 'cart:update',cart,onSuccessDo:open ? 'force_open_drawer' : undefined}}));
    }
    async rollback(before, attempt, discountAttempted) {
      let cart = await this.cart();
      // Only remove lines added by THIS attempt; never replace the customer's cart.
      let owned = (cart.items || []).filter(item => item.properties?._mr_gift_attempt === attempt);
      while (owned.length) {
        cart = await this.request(this.config.changeUrl, {id:owned[0].key,quantity:0});
        const remaining = (cart.items || []).filter(item => item.properties?._mr_gift_attempt === attempt);
        if (remaining.length >= owned.length) throw new Error('Gift removal could not be confirmed.');
        owned = remaining;
      }
      if (discountAttempted) {
        const previous = this.codes(before);
        const otherCodes = this.codes(cart).filter(code => code.toLowerCase() !== this.config.code.toLowerCase());
        cart = await this.request(this.config.updateUrl, {discount:uniqueCodes([...previous,...otherCodes]).join(',')});
      }
      return cart;
    }
    async claim() {
      if (!this.button || this.button.disabled || this.busy || this.reviewBag || !this.config.unlocked || this.config.editor || this.option?.dataset.available !== 'true' || claimInProgress) return;
      this.busy = claimInProgress = true;
      this.button.disabled = this.select.disabled = true;
      this.button.setAttribute('aria-busy','true');
      this.message('Adding your gift and checking the discount…');
      try {
        // Serialise claims across tabs when the browser supports Web Locks.
        if (window.navigator?.locks?.request) await window.navigator.locks.request('moonreed-welcome-gift', () => this.performClaim());
        else await this.performClaim();
      } finally {
        this.busy = claimInProgress = false;
        this.button.removeAttribute('aria-busy');
        this.select.disabled = false;
        this.sync();
      }
    }
    async performClaim() {
      let before, discountAttempted = false, addAttempted = false, verified = false;
      const attempt = window.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
      try {
        before = await this.cart();
        const existing = this.gifts(before);
        if (existing.length) {
          this.reviewBag = true;
          if (existing.length === 1 && this.isFree(existing[0]) && this.hasCode(before,this.config.code)) {
            verified = true;
            this.message('Your complimentary gift is already in your bag.',true);
            await this.publishCart(before,true);
          } else this.message('A welcome gift is already in your bag. Please review it before claiming another.',true);
          return;
        }
        const codes = uniqueCodes([...this.codes(before), this.config.code]);
        discountAttempted = true;
        // Attach the real Shopify discount before adding; never change displayed prices.
        await this.request(this.config.updateUrl, {discount:codes.join(',')});
        addAttempted = true;
        await this.request(this.config.addUrl, {items:[{id:this.option.value,quantity:1,properties:{_mr_newsletter_gift:this.config.campaign,_mr_gift_attempt:attempt}}]});
        // Reapply against the actual items, including an initially empty bag.
        // Keep any other codes now on the cart; this never adds a second item.
        const afterAdd = await this.cart();
        const currentCodes = uniqueCodes([...this.codes(afterAdd), ...codes]);
        await this.request(this.config.updateUrl, {discount:currentCodes.join(',')});
        const cart = await this.cart();
        const gifts = this.gifts(cart);
        const priorCodesRetained = this.codes(before,true).every(code => this.hasCode(cart,code));
        if (gifts.length !== 1 || gifts[0].properties?._mr_gift_attempt !== attempt || !this.isFree(gifts[0]) || !this.hasCode(cart,this.config.code) || !priorCodesRetained) {
          const reason = !priorCodesRetained ? 'This offer cannot be combined with a discount already in your bag.' : !this.hasCode(cart,this.config.code) ? 'The code is not currently eligible for this bag.' : 'The selected gift was not reduced to £0.';
          const error = new Error(reason + ' Your code is still available below to use at checkout.');
          error.giftValidation = true;
          // Some cart-transform apps replace products or strip properties. We
          // cannot safely identify those lines for removal or claim success.
          error.unconfirmedGift = !(cart.items || []).some(item => item.properties?._mr_gift_attempt === attempt);
          throw error;
        }
        verified = true;
        this.reviewBag = true;
        this.button.textContent = 'Gift added';
        this.message('Your complimentary gift is in your bag. Keep exploring or check out when you are ready.',true);
        await this.publishCart(cart,true);
      } catch (error) {
        if (verified) {
          // A display failure must never repeat a successful cart mutation.
          this.reviewBag = true;
          this.message('Your gift is in your bag with its discount applied. Open your bag to continue.',true);
        } else if (before && (discountAttempted || addAttempted)) {
          try {
            const restored = await this.rollback(before,attempt,discountAttempted);
            // A timed-out add may finish later. Do not issue another add from this page.
            this.reviewBag = !!error.unconfirmedGift || (addAttempted && !error.responseKnown && !error.giftValidation);
            this.message(this.reviewBag ? 'We could not confirm this gift claim. Please check your bag before checkout.' : 'The gift was not added. ' + (error.message || 'Please try again later.'),true);
            try { await this.publishCart(restored); } catch { document.dispatchEvent(new CustomEvent('cart:refresh')); }
          } catch {
            this.reviewBag = true;
            this.message('We could not confirm the gift discount or its removal. Please check your bag and remove any gift showing a price before checkout.',true);
          }
        } else {
          this.message('We could not check your bag. Please try again.',true);
        }
      }
    }
  }
  customElements.define('mr-newsletter-gift', NewsletterGift);
})();
